//src/pages/intelDashboard.jsx

import { useEffect, useState } from "react";
import API from "../services/api";
import CreatorDetails from "../components/CreatorDetails";
import CreatorWizard from "../components/CreatorWizard";
import CreatorTable, { formatNumber, scoreColor } from "../components/CreatorTable";

import "./IntelDashboard.css"; 

import { Pie, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement
} from "chart.js";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement
);

export default function IntelDashboard() {
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [editingCreator, setEditingCreator] = useState(null); // NEW

  // filters
  const [search, setSearch] = useState("");
  const [platform, setPlatform] = useState("");
  const [niche, setNiche] = useState("");
  const [sortBy, setSortBy] = useState("score");
  const [activeTab, setActiveTab] = useState("all");

  useEffect(() => {
    const fetchCreators = async () => {
      try {
        const res = await API.get("/creators");
        setCreators(res.data || []);
      } catch (err) {
        console.error("Failed to load creators", err);
      } finally {
        setLoading(false);
      }
    };
    fetchCreators();
  }, []);

  // FILTER + SEARCH + SORT
  const filtered = creators
    .filter((c) => {
      const matchesSearch =
        c.creator_name?.toLowerCase().includes(search.toLowerCase()) ||
        c.creator_url?.toLowerCase().includes(search.toLowerCase()) ||
        c.niche?.toLowerCase().includes(search.toLowerCase());

      const matchesPlatform = platform ? c.platform === platform : true;
      const matchesNiche = niche ? c.niche === niche : true;

      return matchesSearch && matchesPlatform && matchesNiche;
    })
    .sort((a, b) => {
      if (sortBy === "score") return (b.score || 0) - (a.score || 0);
      if (sortBy === "followers") return (b.followers || 0) - (a.followers || 0);
      if (sortBy === "name") return (a.creator_name || "").localeCompare(b.creator_name || "");
      return 0;
    });

  // HEADLINE STATS
  const totalCreators = creators.length;
  const totalReach = creators.reduce((acc, c) => acc + (c.followers || 0), 0);
  const topPerformer = creators.length > 0
    ? creators.reduce((best, c) => (c.score || 0) > (best.score || 0) ? c : best, creators[0])
    : null;
  const avgEngagementRate = creators.length > 0
    ? Math.round(
        creators.reduce((acc, c) => acc + ((c.avg_likes || 0) / (c.followers || 1)), 0) / creators.length * 100
      )
    : 0;

  // Trending hooks
  const hookCounts = creators.reduce((acc, c) => {
    if (c.top_hook) acc[c.top_hook] = (acc[c.top_hook] || 0) + 1;
    return acc;
  }, {});
  const topHooks = Object.entries(hookCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  const hookData = {
    labels: Object.keys(hookCounts),
    datasets: [{
      data: Object.values(hookCounts),
      backgroundColor: ["#22c55e","#f59e0b","#ef4444","#3b82f6","#a855f7"]
    }],
  };

  // Niche distribution
  const nicheCounts = creators.reduce((acc, c) => {
    if (c.niche) acc[c.niche] = (acc[c.niche] || 0) + 1;
    return acc;
  }, {});
  const nicheData = {
    labels: Object.keys(nicheCounts),
    datasets: [{
      data: Object.values(nicheCounts),
      backgroundColor: ["#14b8a6","#f97316","#e11d48","#6366f1","#84cc16"],
    }],
  };

  // 🚀 Fastest Growing Creators
  const fastestGrowing = [...creators]
    .map(c => ({
      ...c,
      growthRatio: (c.avg_views || 0) / (c.followers || 1)
    }))
    .sort((a, b) => b.growthRatio - a.growthRatio)
    .slice(0, 3);

  const growthData = {
    labels: fastestGrowing.map(c => c.creator_name),
    datasets: [{
      label: "Growth Ratio (Views/Followers)",
      data: fastestGrowing.map(c => c.growthRatio.toFixed(2)),
      backgroundColor: ["#22c55e","#3b82f6","#ef4444"]
    }]
  };

  const onBarClick = (event, elements) => {
    if (!elements.length) return;
    const index = elements[0].index;
    setSelected(fastestGrowing[index]);
  };

  // 📈 Emerging Niche Trends
  const nicheTrend = {};
  creators.forEach(c => {
    const niche = c.niche || "Other";
    const week = new Date(c.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" });
    if (!nicheTrend[niche]) nicheTrend[niche] = {};
    nicheTrend[niche][week] = (nicheTrend[niche][week] || 0) + 1;
  });

  const topNiches = Object.entries(nicheTrend)
    .sort((a, b) => Object.values(b[1]).reduce((x,y)=>x+y,0) - Object.values(a[1]).reduce((x,y)=>x+y,0))
    .slice(0,3);

  const labels = [...new Set(creators.map(c =>
    new Date(c.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })
  ))].sort();

  const nicheTrendData = {
    labels,
    datasets: topNiches.map(([niche, data], idx) => ({
      label: niche,
      data: labels.map(l => data[l] || 0),
      borderColor: ["#22c55e","#3b82f6","#ef4444"][idx],
      backgroundColor: ["rgba(34,197,94,0.2)","rgba(59,130,246,0.2)","rgba(239,68,68,0.2)"][idx],
      fill: true,
    }))
  };

  // Pagination
  const pageSize = 4;
  const [currentPage, setCurrentPage] = useState(1);
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const totalPages = Math.ceil(filtered.length / pageSize);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this creator?")) return;
    try {
      await API.delete(`/creators/${id}`);
      setCreators((prev) => prev.filter((c) => c.id !== id));
      setSelected(null);
    } catch (err) {
      alert("Delete failed");
    }
  };

  const handleFinish = async () => {
    // refresh creators after edit/save
    try {
      const res = await API.get("/creators");
      setCreators(res.data || []);
    } catch (err) {
      console.error("Failed to reload creators", err);
    }
    setEditingCreator(null);
  };



  if (loading) return <div className="container">Loading intelligence database...</div>;

  return (


    <div className="container">
      <h1 className="title">🧠 Creator Intelligence Database</h1>
      <p className="subtitle">Search, filter, and analyze creators like a Bloomberg terminal.</p>

       {/*<ScrapeCreator userId={"<current-user-uuid"} />*/}


      {/* HEADLINE STATS */}
      <div className="statsRow">
        <Stat label="Total Creators" value={formatNumber(totalCreators)} />
        <Stat label="Total Reach" value={formatNumber(totalReach)} />
        <Stat label="Top Performer" value={topPerformer ? topPerformer.creator_name : "-"} />
        <Stat label="Avg Engagement Rate" value={`${avgEngagementRate}%`} />
      </div>

      {/* FILTERS */}
      <div className="filters">
        <input className="input" placeholder="Search creators..." value={search} onChange={(e) => setSearch(e.target.value)} />
        <select className="select" value={platform} onChange={(e) => setPlatform(e.target.value)}>
          <option value="">All Platforms</option>
          <option value="TikTok">TikTok</option>
          <option value="Instagram">Instagram</option>
          <option value="YouTube">YouTube</option>
        </select>
        <input className="input" placeholder="Niche (Beauty, Fitness...)" value={niche} onChange={(e) => setNiche(e.target.value)} />
        <select className="select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          <option value="score">Sort: Score</option>
          <option value="followers">Sort: Followers</option>
          <option value="name">Sort: Name</option>
        </select>
      </div>

      {/* TABS */}
      <div className="tabs">
        <button className={activeTab==="all" ? "active" : ""} onClick={() => setActiveTab("all")}>All Creators</button>
        <button className={activeTab==="fastest" ? "active" : ""} onClick={() => setActiveTab("fastest")}>Fastest Growing</button>
        <button className={activeTab==="niches" ? "active" : ""} onClick={() => setActiveTab("niches")}>Emerging Niches</button>
        <button className={activeTab==="hooks" ? "active" : ""} onClick={() => setActiveTab("hooks")}>Trending Hooks</button>
        <button className={activeTab==="distribution" ? "active" : ""} onClick={() => setActiveTab("distribution")}>Niche Distribution</button>
      </div>

      {/* TABLES */}
      {activeTab === "all" && (
        <CreatorTable
          data={paginated}
          onSelect={setSelected}
          columns={[
            { key: "creator_name", label: "Creator" },
            { key: "niche", label: "Niche", render: (val) => <span className="badge">{val || "-"}</span> },
            { key: "followers", label: "Followers", render: (val) => formatNumber(val) },
            { key: "score", label: "Score", render: (val) => (
                <span className={`scoreBadge ${scoreColor(val)}`}>{val || 0}</span>
              ) },
            { key: "platform", label: "Platform" }
          ]}
        />
      )}

      {activeTab === "fastest" && (
        <CreatorTable
          data={fastestGrowing}
          onSelect={setSelected}
          columns={[
            { key: "creator_name", label: "Creator" },
            { key: "followers", label: "Followers", render: (val) => formatNumber(val) },
            { key: "avg_views", label: "Avg Views", render: (val) => formatNumber(val) },
            { key: "growthPercent", label: "Growth %", render: (_, item) => {
                const growthPercent = ((item.avg_views || 0) / (item.followers || 1)) * 100;
                const isPositive = growthPercent >= 0;
                return (
                  <span style={{ color: isPositive ? "green" : "red", fontWeight: "bold" }}>
                    {isPositive ? "↑" : "↓"} {growthPercent.toFixed(0)}%
                  </span>
                );
              }},
            { key: "growthRatio", label: "Growth Ratio", render: (val) => `${val.toFixed(2)}×` }
          ]}
        />
      )}

      {activeTab === "niches" && (
        <CreatorTable
          data={topNiches.map(([niche, data]) => ({
            niche,
            latest: labels.length ? data[labels[labels.length - 1]] || 0 : 0,
            change: labels.length > 1
              ? ((data[labels[labels.length - 1]] || 0) - (data[labels[labels.length - 2]] || 0)) /
                (data[labels[labels.length - 2]] || 1) * 100
              : 0
          }))}
          columns={[
            { key: "niche", label: "Niche" },
            { key: "latest", label: "Latest Count" },
            { key: "change", label: "Trend %", render: (val) => {
                const isPositive = val >= 0;
                return (
                  <span style={{ color: isPositive ? "green" : "red", fontWeight: "bold" }}>
                    {isPositive ? "↑" : "↓"} {val.toFixed(0)}%
                  </span>
                );
              }}
          ]}
        />
      )}

      {activeTab === "hooks" && (
        <CreatorTable
          data={topHooks.map(([hook, count]) => ({ hook, count }))}
          columns={[
            { key: "hook", label: "Hook" },
            { key: "count", label: "Count" }
          ]}
        />
      )}

      {activeTab === "distribution" && (
        <CreatorTable
          data={Object.entries(nicheCounts).map(([niche, count]) => ({ niche, count }))}
          columns={[
            { key: "niche", label: "Niche" },
            { key: "count", label: "Count" }
          ]}
        />
      )}

      {/* CHARTS */}
      <div className="chartsRow">
        <div className="chartBox">
          <h2 className="subtitle">🔥 Top 5 Trending Hooks</h2>
          <Pie data={hookData} />
        </div>
        <div className="chartBox">
          <h2 className="subtitle">🌐 Niche Distribution</h2>
          <Pie data={nicheData} />
        </div>
        <div className="chartBox" style={{ flex: 1 }}>
          <h2 className="subtitle">🚀 Fastest Growing Creators</h2>
          <Pie data={growthData} options={{ onClick: onBarClick }} />
        </div>
        <div className="chartBox">
          <h2 className="subtitle">📈 Emerging Niches</h2>
          <Bar data={nicheTrendData} />
        </div>
      </div>

      {/* PAGINATION (only for All Creators tab) */}
      {activeTab === "all" && (
        <div className="pagination">
          <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Prev</button>
          <span>Page {currentPage} of {totalPages}</span>
          <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Next</button>
        </div>
      )}

      {/* DETAILS MODAL */}
      {selected && (
        <CreatorDetails
          creator={selected}
          onClose={() => setSelected(null)}
          onDelete={(id) => handleDelete(id)}
          onEdit={(c) => {
            setEditingCreator(c); //open wizard
            setSelected(null); //close details panel
          }}
        />
      )}
      {/* WIZARD MODAL */}
      {editingCreator && (
        <div className="overlay">
          <div className="wizard-modal">
            <CreatorWizard
              creator={editingCreator}
              onFinish={handleFinish}
            />
            <button className="close-btn" onClick={() => setEditingCreator(null)}>✕</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* =========================
   SMALL UI COMPONENTS
========================= */
function Stat({ label, value }) {
  return (
    <div className="statBox">
      <div style={{ fontSize: 13, opacity: 0.7 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: "bold" }}>{value}</div>
    </div>
  );
}
